import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import Clapboard from "./Clapboard";
import Wordmark from "./Wordmark";
import AppleLogo from "./AppleLogo";

/**
 * Hero — official BlinkShots brand layout.
 * • Clapboard SVG centered ABOVE the wordmark.
 * • Wordmark "BlinkShots" as ONE continuous word: Blink (white) + Shots (red), bloom glow.
 */
export const Hero = ({ onBookNow, onJoin }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const rotateX = useTransform(scrollYProgress, [0, 1], [0, 18]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  // Mouse parallax for floating cards
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 14 });
  const sy = useSpring(my, { stiffness: 80, damping: 14 });

  useEffect(() => {
    const onMouseMove = (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2;
      const ny = (e.clientY / window.innerHeight - 0.5) * 2;
      mx.set(nx);
      my.set(ny);
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [mx, my]);

  const cardA_rx = useTransform(sy, [-1, 1], [16, -16]);
  const cardA_ry = useTransform(sx, [-1, 1], [-18, 18]);
  const cardB_rx = useTransform(sy, [-1, 1], [-14, 14]);
  const cardB_ry = useTransform(sx, [-1, 1], [12, -12]);
  const cardC_rx = useTransform(sy, [-1, 1], [8, -8]);
  const cardC_ry = useTransform(sx, [-1, 1], [-8, 8]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden perspective-scene cinema-gradient"
    >
      {/* ambient grid */}
      <div className="absolute inset-0 grid-lines opacity-60 [mask-image:radial-gradient(circle_at_center,black,transparent_80%)]" />

      {/* layered ambient — minimal, cinematic, not overexposed */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full blur-[120px] opacity-22"
        style={{ background: "radial-gradient(closest-side, #E10600, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 -right-32 h-[460px] w-[460px] rounded-full blur-[120px] opacity-16"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/4 -left-32 h-[420px] w-[420px] rounded-full blur-[120px] opacity-12"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />

      <div className="starfield opacity-40" />
      <div className="starfield-dense opacity-25" />
      <div className="neon-streak top-[12%] left-[-10%] right-[-10%] opacity-40" />
      <div className="neon-streak bottom-[18%] left-[-10%] right-[-10%] opacity-40" />

      {/* soft vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <div className="absolute inset-0 noise-overlay" />

      <motion.div
        style={{ rotateX, y, opacity }}
        className="relative z-10 mx-auto flex max-w-7xl flex-col items-center px-5 pt-32 pb-20 md:pt-40"
      >
        {/* Wordmark block: Clapboard ABOVE the wordmark, then the one-word "BlinkShots" */}
        <motion.h1
          initial={{ opacity: 0, y: 40, rotateX: 20 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col items-center"
          data-testid="hero-wordmark"
        >
          {/* Clapboard above, centered, elegant size */}
          <span
            aria-hidden
            className="block mb-2 md:mb-3"
            data-testid="hero-clapboard-wrapper"
          >
            <Clapboard
              scrollRef={ref}
              sizeClass="h-14 sm:h-20 md:h-24 lg:h-28"
            />
          </span>

          {/* Wordmark — one word, Blink (white) + Shots (red), clean & sharp */}
          <Wordmark size="hero" />
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-2xl text-center font-display text-2xl md:text-3xl text-neutral-100"
          data-testid="hero-subhead"
        >
          Fast, high-quality content
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 flex flex-col items-center gap-2 max-w-xl text-center text-neutral-400 font-body text-base md:text-lg"
          data-testid="hero-support"
        >
          <span className="inline-flex items-center gap-2 flex-wrap justify-center">
            Shot fast. Edited fast. Shot on latest
            <AppleLogo className="h-[1em] w-auto text-white" />
            <span className="text-white font-medium">iPhone</span>.
          </span>
          <span>
            Content delivered in <span className="text-[#E10600] font-medium">Blinks</span>.
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-3"
        >
          <button
            onClick={onBookNow}
            data-testid="hero-book-now-button"
            className="btn-3d group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-medium text-white"
          >
            <span className="relative z-10">Book Now</span>
            <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={onJoin}
            data-testid="hero-join-button"
            className="btn-3d-ghost group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-medium text-white"
          >
            <Play className="relative z-10 h-4 w-4 text-[#E10600]" />
            <span className="relative z-10">Join the Company</span>
          </button>
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs text-neutral-500">
          <span className="mono-eyebrow">10 min turnaround</span>
          <span className="h-1 w-1 rounded-full bg-neutral-600" />
          <span className="mono-eyebrow">50+ Brand Shoots</span>
          <span className="h-1 w-1 rounded-full bg-neutral-600" />
          <span className="mono-eyebrow">Reels · Ads · UGC</span>
        </div>
      </motion.div>

      {/* Floating 3D centerpiece cards */}
      <div className="pointer-events-none absolute inset-0 z-0 preserve-3d">
        <motion.div
          style={{ rotateX: cardA_rx, rotateY: cardA_ry }}
          className="absolute left-[6%] top-[46%] hidden md:block"
        >
          <FloatingCameraCard />
        </motion.div>

        <motion.div
          style={{ rotateX: cardB_rx, rotateY: cardB_ry }}
          className="absolute right-[4%] top-[40%] hidden md:block"
        >
          <FloatingStatCard />
        </motion.div>

        <motion.div
          style={{ rotateX: cardC_rx, rotateY: cardC_ry }}
          className="absolute left-1/2 -translate-x-1/2 bottom-[12%] md:bottom-[6%]"
        >
          <FloatingReelCard />
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E10600]/60 to-transparent" />
    </section>
  );
};

const FloatingCameraCard = () => (
  <div
    className="animate-float w-60 rounded-2xl glass p-4"
    style={{ transform: "translateZ(80px)" }}
    data-testid="hero-card-camera"
  >
    <div className="flex items-center gap-3">
      <div className="relative h-10 w-10 overflow-hidden rounded-xl bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,#E10600,transparent_60%)]" />
        <div className="absolute inset-0 grid place-items-center">
          <div className="h-5 w-5 rounded-full border-2 border-white" />
        </div>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
          Live Shoot
        </p>
        <p className="text-sm text-white">Soho · 4K 60fps</p>
      </div>
    </div>
    <div className="mt-3 flex justify-between text-[10px] font-mono text-neutral-400">
      <span>REC</span>
      <span className="text-[#E10600]">● 00:42:11</span>
    </div>
    <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-[#E10600] to-[#ff7070]" />
    </div>
  </div>
);

const FloatingStatCard = () => (
  <div
    className="animate-float w-56 rounded-2xl glass p-5"
    style={{ animationDelay: "1.2s", transform: "translateZ(120px)" }}
    data-testid="hero-card-stat"
  >
    <p className="mono-eyebrow">Reel Performance</p>
    <p className="mt-2 font-display text-3xl text-white">+238%</p>
    <p className="text-xs text-neutral-400 mt-1">Avg. reach uplift after BlinkShots edit</p>
    <svg className="mt-3" viewBox="0 0 200 40" width="100%" height="40">
      <polyline
        points="0,30 20,25 40,28 60,18 80,22 100,12 120,14 140,6 160,10 180,4 200,8"
        fill="none"
        stroke="#E10600"
        strokeWidth="2"
      />
    </svg>
  </div>
);

const FloatingReelCard = () => (
  <div
    className="animate-float rounded-2xl glass p-3 flex items-center gap-3"
    style={{ animationDelay: "0.6s", transform: "translateZ(40px)" }}
    data-testid="hero-card-reel"
  >
    {/* Mini cinematic clapboards matching the hero brand */}
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="relative h-14 w-12 md:h-16 md:w-14 rounded-md overflow-hidden bg-[#0a0a0a] border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
      >
        {/* slate stripes (top half) */}
        <div className="absolute top-0 left-0 right-0 h-1/2 bg-[#0a0a0a] border-b border-white/15">
          <div className="absolute inset-0 grid grid-cols-3">
            <div className="bg-white/85 [clip-path:polygon(20%_0,100%_0,80%_100%,0%_100%)]" />
            <div className="bg-[#0a0a0a] [clip-path:polygon(20%_0,100%_0,80%_100%,0%_100%)]" />
            <div className="bg-white/85 [clip-path:polygon(20%_0,100%_0,80%_100%,0%_100%)]" />
          </div>
        </div>
        {/* base */}
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-[#0a0a0a]">
          <div className="absolute top-1.5 left-1.5 right-2 h-px bg-white/15" />
          <div className="absolute top-3 left-1.5 right-3 h-px bg-white/10" />
          <div className="absolute bottom-1.5 right-1.5 h-1 w-1 rounded-full bg-[#E10600]" />
        </div>
      </div>
    ))}
    <div className="pl-1 pr-2 text-[10px] font-mono text-neutral-400">
      <div className="uppercase tracking-widest text-neutral-500">Reel Queue</div>
      <div className="text-white text-[11px] mt-0.5">3 ready</div>
    </div>
  </div>
);

export default Hero;
